import { Flex, Text, useDisclosure } from "@chakra-ui/react";
import PageTitle from "../components/PageTitle";
import DataTable, {
	type Cell,
	SearchBar,
} from "../components/table/ServerSideTable";
import { useCenterPatientsPaginated, useCenters } from "../hooks/centers.hooks";
import { useMemo, useRef, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { Patient } from "../types/patient.types";
import { format } from "date-fns";
import PrimaryButton from "../components/PrimaryButton";
import { AddIcon } from "@chakra-ui/icons";
import SimpleModal from "../components/modals/SimpleModal";
import PatientForm, {
	type PatientFormHandler,
} from "../components/forms/PatientForm";
import { useMutation, useQueryClient } from "react-query";
import { createPatient, updatePatient } from "../mutations/patient.mutations";
import PenIcon from "../components/icons/PenIcon";
import PatientStatCommand from "./PatientStatCommand";
import { useConnectedUser } from "../hooks/user.hooks";
import { usePatientsPaginated } from "../hooks/patients.hooks";
import type { Center } from "../types/center.types";
import CenterText from "../components/CenterText";

const MemberPatientsPage = () => {
	const disclosure = useDisclosure();
	const { center } = useCenters();
	const formRef = useRef<PatientFormHandler>(null);
	const columns = useMemo<ColumnDef<Patient>[]>(
		() => [
			{
				accessorFn: (row) => `${row.firstName} ${row.lastName}`,
				id: "patient",
				header: "Patient",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) =>
					row.birthDate && format(row.birthDate, "dd/MM/yyyy"),
				id: "birthDate",
				header: "Date de naissance",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <PatientStatCommand id={row.id} />,
				id: "orders",
				header: "Commandes",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	const queryClient = useQueryClient();
	const handleClose = () => {
		if (formRef.current) {
			formRef.current.reset();
		}
		disclosure.onClose();
		setPatient(undefined);
	};
	const { mutate: mutateCreatePatient } = useMutation(createPatient, {
		onSuccess: () => {
			handleClose();
			queryClient.invalidateQueries("center-patients-paginated");
		},
	});
	const { mutate: mutateUpdatePatient } = useMutation(updatePatient, {
		onSuccess: () => {
			handleClose();
			queryClient.invalidateQueries("center-patients-paginated");
		},
	});
	const [patient, setPatient] = useState<Patient | undefined>(undefined);
	const PatientsActionsComponent = () => {
		return (
			<>
				<PrimaryButton w="fit-content" onClick={disclosure.onOpen}>
					<AddIcon mr={4} />
					Ajouter un patient
				</PrimaryButton>
			</>
		);
	};
	const handleClick = async () => {
		if (formRef.current && center?.id) {
			const valid = await formRef.current.valid();
			if (valid) {
				const values = formRef.current.values();
				if (patient?.id === null || patient?.id === undefined) {
					mutateCreatePatient({
						patient: {
							firstName: values.firstName,
							lastName: values.lastName,
							birthDate: values.birthDate,
							centerId: center.id,
						},
					});
				} else {
					mutateUpdatePatient({
						patient: {
							id: patient?.id,
							firstName: values.firstName,
							lastName: values.lastName,
							birthDate: values.birthDate,
							centerId: center.id,
						},
					});
				}
			}
		}
	};

	return (
		<>
			<Flex>
				<PageTitle>Patients</PageTitle>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={useCenterPatientsPaginated({ id: center?.id })}
				w={["400px", "200px", "auto"]}
				flexGrow={[0, 0, 1]}
				toolbar={
					<Flex my="1rem" justifyContent="space-between">
						<SearchBar />
						<PatientsActionsComponent />
					</Flex>
				}
				cellLink={(cell: Cell) => {
					return `/patients/${cell.row.id}`;
				}}
			></DataTable>

			<SimpleModal
				title={patient ? "Modifier un patient" : "Ajouter un patient"}
				acceptText={patient ? "Modifier" : "Sauvegarder"}
				icon={
					patient ? (
						<PenIcon color="brand.700" />
					) : (
						<AddIcon color="brand.700" />
					)
				}
				isOpen={disclosure.isOpen}
				onClose={handleClose}
				onClick={handleClick}
			>
				<PatientForm ref={formRef} value={patient} />
			</SimpleModal>
		</>
	);
};

const AdminPatientsPage = () => {
	const disclosure = useDisclosure();
	const { center } = useCenters();
	const formRef = useRef<PatientFormHandler>(null);
	const columns = useMemo<ColumnDef<Patient>[]>(
		() => [
			{
				accessorFn: (row) => `${row.firstName} ${row.lastName}`,
				id: "patient",
				header: "Patient",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<CenterText
						id={row.centerId}
						format={(center: Center) => `${center.name}`}
					/>
				),
				id: "center",
				header: "Centre",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) =>
					row.birthDate && format(row.birthDate, "dd/MM/yyyy"),
				id: "birthDate",
				header: "Date de naissance",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => <PatientStatCommand id={row.id} />,
				id: "orders",
				header: "Commandes",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
			{
				accessorFn: (row) => (
					<Flex justify="end" w="100%">
						<Text
							color="brand.700"
							textDecoration={"underline"}
							onClick={() => {
								setPatient(row);
								disclosure.onOpen();
							}}
						>
							Modifier le patient
						</Text>
					</Flex>
				),
				id: "actions",
				header: "",
				cell: (info) => info.getValue(),
				sortDescFirst: true,
			},
		],
		[],
	);
	const queryClient = useQueryClient();
	const handleClose = () => {
		if (formRef.current) {
			formRef.current.reset();
		}
		disclosure.onClose();
		setPatient(undefined);
	};
	const { mutate: mutateCreatePatient } = useMutation(createPatient, {
		onSuccess: () => {
			handleClose();
			queryClient.invalidateQueries("center-patients-paginated");
		},
	});
	const { mutate: mutateUpdatePatient } = useMutation(updatePatient, {
		onSuccess: () => {
			handleClose();
			queryClient.invalidateQueries("center-patients-paginated");
		},
	});
	const [patient, setPatient] = useState<Patient | undefined>(undefined);
	const handleClick = async () => {
		if (formRef.current && center?.id) {
			const valid = await formRef.current.valid();
			if (valid) {
				const values = formRef.current.values();
				if (patient?.id === null || patient?.id === undefined) {
					mutateCreatePatient({
						patient: {
							firstName: values.firstName,
							lastName: values.lastName,
							birthDate: values.birthDate,
						},
					});
				} else {
					mutateUpdatePatient({
						patient: {
							id: patient?.id,
							firstName: values.firstName,
							lastName: values.lastName,
							birthDate: values.birthDate,
						},
					});
				}
			}
		}
	};

	return (
		<>
			<Flex>
				<PageTitle>Patients</PageTitle>
			</Flex>

			<DataTable
				columns={columns}
				paginatedQuery={usePatientsPaginated()}
				w={["400px", "300px", "200px", "140px", "200px"]}
				flexGrow={[0, 0, 0, 1, 1]}
				cellLink={(cell: Cell) => {
					if (cell.id.endsWith("actions")) {
						return;
					}
					return `/patients/${cell.row.id}`;
				}}
			></DataTable>

			<SimpleModal
				title={patient ? "Modifier un patient" : "Ajouter un patient"}
				acceptText={patient ? "Modifier" : "Sauvegarder"}
				icon={
					patient ? (
						<PenIcon color="brand.700" />
					) : (
						<AddIcon color="brand.700" />
					)
				}
				isOpen={disclosure.isOpen}
				onClose={handleClose}
				onClick={handleClick}
			>
				<PatientForm ref={formRef} value={patient} />
			</SimpleModal>
		</>
	);
};

const PatientsPage = () => {
	const user = useConnectedUser();

	if (user === undefined || user === null) {
		return <></>;
	}
	if (user.isSuperAdmin) {
		return <AdminPatientsPage />;
	}
	return <MemberPatientsPage />;
};

export default PatientsPage;
